import {
  createContext, useCallback, useContext, useMemo, useState,
} from 'react';

import type { ReactNode } from 'react';

interface ISidePanelSlotContextValue {
  slot: HTMLDivElement | null,
  setSlot: (node: HTMLDivElement | null) => void,
  activePanelId: string | null,
  claim: (panelId: string) => void,
  release: (panelId: string) => void,
}

const SidePanelSlotContext = createContext<ISidePanelSlotContextValue>({
  slot: null,
  setSlot: () => {},
  activePanelId: null,
  claim: () => {},
  release: () => {},
});

/**
 * Holds the DOM node a screen's own side panels (filters, scenarios, and the like) portal into,
 * and which one of them is allowed to actually show.
 *
 * The slot has exactly one screen behind it at a time, but the panels that can appear there are
 * a whole family of always-mounted components, each opened and closed by its own corner of a
 * large, easy-to-miss-a-spot screen. `claim`/`release` are the seam that keeps that mess from
 * reaching the slot: whichever panel last claimed it is the only one `SidePanel` ever shows, no
 * matter how many of the others still believe they are open.
 *
 * Wraps the page layout; `SidePanelSlot` renders the node itself wherever the layout wants the
 * free space split, so a screen's panels never have to know where that is, only that it exists.
 * Independent of `DockRightSlotContext`: the AI chat docks into its own slot, further out, so the
 * two can sit side by side instead of competing for the same box.
 */
function SidePanelSlotProvider({ children }: { children: ReactNode }) {
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  const [activePanelId, setActivePanelId] = useState<string | null>(null);

  const claim = useCallback((panelId: string) => {
    setActivePanelId(panelId);
  }, []);

  // Only clears the claim if it is still this panel's: an out-of-order release from a panel that
  // has already lost the claim must not drop the one that took it.
  const release = useCallback((panelId: string) => {
    setActivePanelId((current) => (current === panelId ? null : current));
  }, []);

  const value = useMemo<ISidePanelSlotContextValue>(
    () => ({
      slot, setSlot, activePanelId, claim, release,
    }),
    [slot, activePanelId, claim, release],
  );

  return (
    <SidePanelSlotContext.Provider value={value}>
      {children}
    </SidePanelSlotContext.Provider>
  );
}

/** The slot's DOM node once `SidePanelSlot` has mounted, or null while there is none to dock into. */
export const useSidePanelSlot = () => useContext(SidePanelSlotContext).slot;

/** What `SidePanelSlot` calls to publish (or clear, on unmount) the node it rendered. */
export const useRegisterSidePanelSlot = () => useContext(SidePanelSlotContext).setSlot;

/** The exclusivity seam `SidePanel` claims and releases against as its own `open` prop changes. */
export const useSidePanelClaim = () => {
  const { activePanelId, claim, release } = useContext(SidePanelSlotContext);

  return { activePanelId, claim, release };
};

export default SidePanelSlotProvider;
